import PolarisLayout from './Polaris';
import EmbeddedLayout from './Embedded';
import AccordionEmbeddedLayout from './AccordionEmbeddedLayout';
import useIsPolaris from '../../hooks/useIsPolaris';
import ErrorBoundary from 'src/components/ErrorBoundary';
import ErrorContainer from '../ErrorContainer';
import useInterview from 'src/interviews/hooks/useInterview';
import useInterviewType from 'src/interviews/hooks/useInterviewType';
import { trackPolarisNavigateBack } from 'src/interviews/analytics';
import { createContext } from 'react';

import type {
  IEmbeddedLayoutProps,
  IAccordionEmbeddedLayoutProps,
  IPolarisLayoutProps,
  LayoutProps,
} from './types';

const LayoutContext = createContext<{
  onGoBackClick?: () => void;
  triggerRestart?: () => void;
}>({});

const Layout = (props: LayoutProps) => {
  const isPolaris = useIsPolaris();

  if (isPolaris) {
    return <PolarisLayout {...(props as IPolarisLayoutProps)} />;
  }

  if ('isAccordionEmbeddedLayout' in props && props.isAccordionEmbeddedLayout) {
    // This layout bakes in an accordion to the module, whereas the EmbeddedLayout assumes any
    // desired accordion is handled by the parent application
    return <AccordionEmbeddedLayout {...(props as IAccordionEmbeddedLayoutProps)} />;
  }

  return <EmbeddedLayout {...(props as IEmbeddedLayoutProps)} />;
};

const LayoutWithErrorBoundary = (props: LayoutProps) => {
  const { token } = useInterview();
  const interviewType = useInterviewType();
  const isPolaris = useIsPolaris();

  const onGoBackClick = () => {
    if (isPolaris) {
      trackPolarisNavigateBack(token || '', interviewType);
    }
    if ('handleGoBack' in props) {
      props.handleGoBack?.();
    }
  };

  const context = {
    onGoBackClick,
    triggerRestart: props.triggerRestart,
  };

  return (
    <ErrorBoundary
      key={token}
      fallback={<ErrorContainer className="bp-my-16" triggerRestart={props.triggerRestart} />}
      interviewToken={token}
      interviewType={interviewType}
      component="Layout"
    >
      <LayoutContext.Provider value={context}>
        <Layout {...props}>{props.children}</Layout>
      </LayoutContext.Provider>
    </ErrorBoundary>
  );
};

export { LayoutContext };

export default LayoutWithErrorBoundary;
